import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { Link } from "react-router-dom";
import { Loader2, ArrowRight, Upload, FileText, X } from "lucide-react";
import { api, formatApiError, EMAIL_PATTERN } from "../../lib/api";
import { CORE_CATEGORIES, AVAILABILITY, ENGAGEMENT_TYPES } from "../../data/options";
import { Field, Fieldset, TextInput, TextArea, SelectInput, CheckRow, FormAlert, SubmissionSuccess } from "./FormKit";

const req = (label) => ({ required: `${label} is required` });
const CV_EXT = ["pdf", "doc", "docx"];
const MAX_CV = 10 * 1024 * 1024;

const CvUpload = ({ file, onChange, error }) => (
  <div className="md:col-span-2">
    <p className="mb-2 block text-sm font-medium text-[#111827]">CV or professional profile <span className="font-normal text-slate-400">(optional)</span></p>
    {file ? (
      <div className="flex items-center justify-between rounded-md border border-slate-300 bg-[#F4F6F8] px-4 py-3" data-testid="cv-selected">
        <span className="flex items-center gap-3 text-sm text-[#111827]"><FileText className="h-4 w-4 text-[#2563EB]" /> {file.name} <span className="text-slate-400">({(file.size / 1024 / 1024).toFixed(1)} MB)</span></span>
        <button type="button" onClick={() => onChange(null)} className="rounded p-1 text-slate-500 hover:text-[#0B1220]" aria-label="Remove file" data-testid="cv-remove-button"><X className="h-4 w-4" /></button>
      </div>
    ) : (
      <label htmlFor="cv-file" className={`flex cursor-pointer flex-col items-center justify-center rounded-md border border-dashed px-6 py-8 text-center transition-colors hover:border-[#2563EB] hover:bg-blue-50/40 ${error ? "border-red-500" : "border-slate-300"}`}>
        <Upload className="h-5 w-5 text-[#2563EB]" aria-hidden="true" />
        <span className="mt-3 text-sm font-medium text-[#0B1220]">Upload CV (PDF, DOC or DOCX, max 10 MB)</span>
        <span className="mt-1 text-xs text-slate-500">Stored privately. Accessible only to authorised Trusted Leap administrators.</span>
        <input id="cv-file" type="file" accept=".pdf,.doc,.docx" className="sr-only" data-testid="cv-file-input" onChange={(e) => onChange(e.target.files?.[0] || null)} />
      </label>
    )}
    {error && <p role="alert" className="mt-1.5 text-xs text-red-600" data-testid="cv-error">{error}</p>}
  </div>
);

export const ExpertApplicationForm = () => {
  const { register, handleSubmit, formState: { errors, isSubmitting } } = useForm({ mode: "onTouched", defaultValues: { engagement_types: [] } });
  const [file, setFile] = useState(null);
  const [fileError, setFileError] = useState("");
  const [result, setResult] = useState(null);
  const [serverError, setServerError] = useState("");
  const inFlight = useRef(false);

  const pickFile = (f) => {
    setFileError("");
    if (f) {
      const ext = f.name.split(".").pop().toLowerCase();
      if (!CV_EXT.includes(ext)) return setFileError("Please upload a PDF, DOC or DOCX file.");
      if (f.size > MAX_CV) return setFileError("File must be 10 MB or smaller.");
    }
    setFile(f);
  };

  const onSubmit = async (values) => {
    if (inFlight.current || fileError) return;
    inFlight.current = true;
    setServerError("");
    const payload = { ...values, years_experience: Number(values.years_experience), affiliation: values.affiliation || null, registration_details: values.registration_details || null };
    const fd = new FormData();
    fd.append("data", JSON.stringify(payload));
    if (file) fd.append("cv", file);
    try {
      const { data } = await api.post("/applications", fd);
      setResult({ ...data, email: values.email });
    } catch (e) {
      setServerError(formatApiError(e));
    } finally {
      inFlight.current = false;
    }
  };

  if (result) {
    return (
      <SubmissionSuccess
        testId="expert-success"
        title="Application received"
        message="Thank you for applying to join the Trusted Leap network. Your application has been received and will be reviewed. Submitting an application does not guarantee acceptance into the network or any engagement opportunities."
        reference={result.reference}
        emailStatus={result.confirmation_email}
        email={result.email}
      />
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-10" data-testid="expert-form">
      <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" {...register("website")} />
      <Fieldset legend="About you">
        <Field id="ex-full_name" label="Full name" required error={errors.full_name}>
          <TextInput id="ex-full_name" autoComplete="name" error={errors.full_name} data-testid="expert-full-name-input" {...register("full_name", req("Full name"))} />
        </Field>
        <Field id="ex-email" label="Professional email" required error={errors.email}>
          <TextInput id="ex-email" type="email" autoComplete="email" error={errors.email} data-testid="expert-email-input" {...register("email", { ...req("Professional email"), pattern: { value: EMAIL_PATTERN, message: "Enter a valid email address" } })} />
        </Field>
        <Field id="ex-country_residence" label="Country of residence" required error={errors.country_residence}>
          <TextInput id="ex-country_residence" autoComplete="country-name" error={errors.country_residence} data-testid="expert-country-input" {...register("country_residence", req("Country of residence"))} />
        </Field>
        <Field id="ex-jurisdictions" label="Jurisdictions qualified to practise or provide services" required error={errors.jurisdictions} hint="List each country or jurisdiction">
          <TextInput id="ex-jurisdictions" error={errors.jurisdictions} data-testid="expert-jurisdictions-input" {...register("jurisdictions", req("Jurisdictions"))} />
        </Field>
      </Fieldset>

      <Fieldset legend="Professional profile">
        <Field id="ex-primary_category" label="Primary service category" required error={errors.primary_category}>
          <SelectInput id="ex-primary_category" options={CORE_CATEGORIES} placeholder="Select category" error={errors.primary_category} data-testid="expert-category-select" {...register("primary_category", req("Primary service category"))} />
        </Field>
        <Field id="ex-years_experience" label="Years of relevant experience" required error={errors.years_experience}>
          <TextInput id="ex-years_experience" type="number" min="0" max="60" error={errors.years_experience} data-testid="expert-years-input" {...register("years_experience", { ...req("Years of experience"), min: { value: 0, message: "Must be 0 or more" }, max: { value: 60, message: "Must be 60 or less" } })} />
        </Field>
        <Field id="ex-specialisations" label="Areas of specialisation" required error={errors.specialisations} className="md:col-span-2">
          <TextArea id="ex-specialisations" rows={3} error={errors.specialisations} data-testid="expert-specialisations-input" {...register("specialisations", req("Areas of specialisation"))} />
        </Field>
        <Field id="ex-qualifications" label="Professional qualifications and certifications" required error={errors.qualifications} className="md:col-span-2">
          <TextArea id="ex-qualifications" rows={3} error={errors.qualifications} data-testid="expert-qualifications-input" {...register("qualifications", req("Qualifications"))} />
        </Field>
        <Field id="ex-affiliation" label="Current professional or business affiliation">
          <TextInput id="ex-affiliation" data-testid="expert-affiliation-input" {...register("affiliation")} />
        </Field>
        <Field id="ex-industries" label="Industries served" required error={errors.industries}>
          <TextInput id="ex-industries" error={errors.industries} data-testid="expert-industries-input" {...register("industries", req("Industries served"))} />
        </Field>
        <Field id="ex-profile_url" label="LinkedIn profile or professional website" required error={errors.profile_url}>
          <TextInput id="ex-profile_url" type="url" placeholder="https://" error={errors.profile_url} data-testid="expert-profile-url-input" {...register("profile_url", { ...req("Profile URL"), pattern: { value: /^https?:\/\/.+\..+/i, message: "Enter a valid URL starting with https://" } })} />
        </Field>
        <Field id="ex-registration_details" label="Professional registration details" hint="Registering body and membership or licence number, where relevant">
          <TextInput id="ex-registration_details" data-testid="expert-registration-input" {...register("registration_details")} />
        </Field>
      </Fieldset>

      <Fieldset legend="Availability">
        <Field id="ex-availability" label="Engagement availability" required error={errors.availability}>
          <SelectInput id="ex-availability" options={AVAILABILITY} placeholder="Select availability" error={errors.availability} data-testid="expert-availability-select" {...register("availability", req("Availability"))} />
        </Field>
        <div className="md:col-span-2">
          <p className="mb-3 text-sm font-medium text-[#111827]">Preferred engagement types <span className="text-[#2563EB]">*</span></p>
          <div className="grid gap-3 sm:grid-cols-2">
            {ENGAGEMENT_TYPES.map((t, i) => (
              <label key={t} className="flex cursor-pointer items-center gap-3 rounded-md border border-slate-200 px-4 py-3 text-sm text-slate-700 transition-colors hover:border-slate-400">
                <input type="checkbox" value={t} className="h-4 w-4 accent-[#2563EB]" data-testid={`expert-engagement-${i}`} {...register("engagement_types", { validate: (v) => (v && v.length > 0) || "Select at least one engagement type" })} />
                {t}
              </label>
            ))}
          </div>
          {errors.engagement_types && <p role="alert" className="mt-1.5 text-xs text-red-600" data-testid="expert-engagement-error">{errors.engagement_types.message}</p>}
        </div>
      </Fieldset>

      <Fieldset legend="Documents">
        <CvUpload file={file} onChange={pickFile} error={fileError} />
      </Fieldset>

      <div className="space-y-4 border-t border-slate-200 pt-8">
        <CheckRow id="ex-privacy_consent" error={errors.privacy_consent} data-testid="expert-privacy-checkbox"
          label={<>I consent to Trusted Leap processing my information as described in the <Link to="/privacy-policy" target="_blank" className="text-[#2563EB] underline underline-offset-2">Privacy Policy</Link>. My profile will not be displayed publicly without my approval and consent.<span className="text-[#2563EB]"> *</span></>}
          {...register("privacy_consent", { required: "Consent is required" })} />
        <CheckRow id="ex-accuracy_declaration" error={errors.accuracy_declaration} data-testid="expert-accuracy-checkbox"
          label={<>I declare that the information submitted is accurate and complete, and I understand that submitting an application does not guarantee acceptance or engagement opportunities.<span className="text-[#2563EB]"> *</span></>}
          {...register("accuracy_declaration", { required: "This declaration is required" })} />
      </div>

      <FormAlert message={serverError} testId="expert-server-error" />

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-slate-500">Fields marked * are required.</p>
        <button type="submit" disabled={isSubmitting} className="btn-primary" data-testid="expert-submit-button">
          {isSubmitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Submitting</> : <>Apply to Join Trusted Leap <ArrowRight className="h-4 w-4" /></>}
        </button>
      </div>
    </form>
  );
};
